<template>
    <div class="nh-billing-page" :style="{ '--nh-footer-height': `${footerHeight}px` }">
        <header class="nh-page-header">
            <button type="button" class="nh-back-button" aria-label="뒤로가기" @click="goBackToNhMain">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
                    stroke-linejoin="round" aria-hidden="true" focusable="false">
                    <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
            </button>
            <h1>청구할인 상세</h1>
        </header>

        <main class="nh-page-body">
            <section class="nh-hero-section">
                <p class="nh-brand-name">{{ displayBrandName }}</p>
                <div class="nh-benefit-row">
                    <div class="nh-title-box">
                        <h2>{{ discountTitle }}</h2>
                    </div>
                    <div class="nh-logo-box">
                        <img :src="makeBrandImageUrl(brandDetail.brandPath)" :alt="displayBrandName" />
                    </div>
                </div>
            </section>

            <section class="nh-summary-card">
                <div class="nh-summary-item">
                    <strong>홈페이지</strong>
                    <button v-if="brandDetail.homepageUrl" type="button" class="nh-homepage-link"
                        @click="openHomepage(brandDetail.homepageUrl)">
                        {{ formatHomepageUrl(brandDetail.homepageUrl) }}
                    </button>
                    <span v-else class="nh-muted-text">홈페이지 정보 없음</span>
                </div>

                <div class="nh-summary-item">
                    <strong>혜택</strong>
                    <p>{{ benefitDescription }}</p>
                </div>
            </section>

            <nav class="nh-tab-list" aria-label="청구할인 상세 탭">
                <button type="button" :class="{ active: activeTab === 'detail' }" @click="activeTab = 'detail'">
                    제휴사 상세 정보
                </button>
                <button type="button" :class="{ active: activeTab === 'stores' }" @click="activeTab = 'stores'">
                    가맹점 리스트
                </button>
            </nav>

            <section v-if="activeTab === 'detail'" class="nh-detail-panel">
                <div v-if="safeBrandDetailView" class="nh-brand-detail" v-html="safeBrandDetailView"></div>
                <div v-else class="nh-empty-detail">
                    <p>{{ displayBrandName }}</p>
                    <strong>사람의 행복을 위해<br />더 나은 공간을 제안하는 기업</strong>
                </div>
            </section>

            <section v-else class="nh-store-panel">
                <div v-if="!isOnlineBrand && regionTabs.length > 0" class="nh-region-list"
                    :class="{ expanded: regionExpanded }">
                    <div class="nh-region-row">
                        <button v-for="region in firstRegionRowTabs" :key="region.value" type="button"
                            :class="{ active: selectedRegion === region.value }" @click="selectRegion(region.value)">
                            {{ region.label }}
                        </button>
                        <button v-if="regionTabs.length > initialRegionCount" type="button"
                            class="nh-region-more-button" :class="{ expanded: regionExpanded }"
                            :aria-label="regionExpanded ? '지역 목록 접기' : '지역 목록 더보기'"
                            @click="regionExpanded = !regionExpanded">
                            <span aria-hidden="true"></span>
                        </button>
                    </div>
                    <div v-if="remainingRegionTabs.length > 0" class="nh-region-row">
                        <button v-for="region in remainingRegionTabs" :key="region.value" type="button"
                            :class="{ active: selectedRegion === region.value }" @click="selectRegion(region.value)">
                            {{ region.label }}
                        </button>
                    </div>
                </div>

                <div v-if="isLoading.search" class="nh-store-loading">가맹점 정보를 불러오는 중입니다.</div>

                <ul v-else-if="displayStoreList.length > 0" class="nh-store-list">
                    <li v-for="(store, index) in displayStoreList"
                        :key="store.storeUuid || `${store.storeName || 'online'}-${index}`" class="nh-store-item"
                        :class="{ 'nh-online-store-item': isOnlineBrand }">
                        <div v-if="isOnlineBrand" class="nh-online-store-main">
                            <div class="nh-online-store-title-row">
                                <strong>온라인</strong>
                                <button type="button" class="nh-map-button disabled" disabled @click.stop>
                                    지도 보기
                                </button>
                            </div>
                            <button v-if="brandDetail.homepageUrl" type="button" class="nh-online-homepage"
                                @click="openHomepage(brandDetail.homepageUrl)">
                                {{ formatHomepageUrl(brandDetail.homepageUrl) }}
                            </button>
                            <span>{{ store.phone || '-' }}</span>
                        </div>
                        <div v-else class="nh-store-main">
                            <div class="nh-store-copy">
                                <strong>{{ store.storeName || '-' }}</strong>
                                <p>{{ store.address || '-' }}</p>
                                <span>{{ store.phone || '-' }}</span>
                            </div>
                            <button type="button" class="nh-map-button" @click="toggleStoreMap(index)">
                                지도 보기
                            </button>
                        </div>

                        <div v-if="selectedStoreIndex === index" class="nh-map-area">
                            <NaverMapMo :lat="store.latitude" :lng="store.longitude" :title="store.storeName"
                                :address="store.address" :is-ready="mapReadyMap[index] === true"
                                @map-loaded="onMapLoaded(index)" @map-error="handleMapError(index)" />
                            <div v-if="!mapLoadedMap[index]" class="nh-map-loading">지도를 불러오는 중...</div>
                        </div>
                    </li>
                </ul>

                <div v-else class="nh-empty-store">해당 지역에 가맹점이 없습니다.</div>

                <button v-if="!isOnlineBrand && hasMoreStores" type="button" class="nh-more-button"
                    @click="loadMoreStores">
                    더보기
                </button>
            </section>

            <div v-if="!hideListButton" class="nh-bottom-action">
                <button type="button" @click="goBackToList">
                    <img src="@/assets/images/brand/btn_home.png" alt="목록으로" />
                </button>
            </div>
        </main>

        <footer ref="nhFooter" class="nh-floating-footer" aria-label="하이비타민 고객센터 정보">
            <div class="nh-footer-inner">
                <div class="nh-footer-company">
                    <strong>(주)브릿지엠</strong>
                    <dl>
                        <div class="nh-footer-meta">
                            <div>
                                <dt>대표이사 :</dt>
                                <dd>서재필</dd>
                            </div>
                            <span class="nh-footer-divider" aria-hidden="true">|</span>
                            <div>
                                <dt>사업자등록번호 :</dt>
                                <dd>113-86-67636</dd>
                            </div>
                        </div>
                        <div class="address">
                            <span class="nh-address-desktop">주소 : 서울시 구로구 디지털로34길 43 코오롱싸이언스<br />밸리1차 609,610호</span>
                            <span class="nh-address-mobile">주소 : 서울시 구로구 디지털로34길 43 코오롱<br />싸이언스 벨리1차 609,610호</span>
                        </div>
                    </dl>
                    <p>Copyright ⓒ 하이비타민. ALL RIGHTS RESERVED.</p>
                </div>

                <div class="nh-footer-cs">
                    <strong>고객센터 전화번호</strong>
                    <a href="tel:0264078800" class="nh-cs-phone">
                        <span class="nh-phone-icon" aria-hidden="true"></span>
                        <span>02.6407.8800</span>
                    </a>
                </div>
            </div>
        </footer>
    </div>
</template>

<script>
import { getAxios } from "@/lib/AxiosCall";
import { DEFAULT_NO_IMAGE } from "@/lib/Constant";
import { getBranchUuid } from "@/lib/Config";
import NaverMapMo from "@/components/NaverMapMo.vue";

export default {
    components: {
        NaverMapMo,
    },
    props: {
        brandUuid: {
            type: String,
            default: '',
        },
        isPopup: {
            type: Boolean,
            default: false,
        },
        hideListButton: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            footerHeight: 0,
            activeTab: 'detail',
            selectedRegion: '',
            regionExpanded: false,
            initialRegionCount: 10,
            selectedStoreIndex: -1,
            currentPage: 1,
            pageSize: 5,
            total: 0,
            branchUuid: getBranchUuid() || '',
            availableRegions: [],
            brandDetail: {},
            storeList: [],
            mapLoadedMap: {},
            mapReadyMap: {},
            isLoading: {
                brand: false,
                search: false,
            },
        };
    },
    computed: {
        regionTabs() {
            if (this.availableRegions.length === 0) return [];
            return [{ label: '전체', value: '' }, ...this.availableRegions.map(region => ({
                label: region,
                value: region,
            }))];
        },
        visibleRegionTabs() {
            return this.regionExpanded ? this.regionTabs : this.regionTabs.slice(0, this.initialRegionCount);
        },
        firstRegionRowTabs() {
            return this.visibleRegionTabs.slice(0, 5);
        },
        remainingRegionTabs() {
            return this.visibleRegionTabs.slice(5);
        },
        isOnlineBrand() {
            const brandType = String(this.brandDetail.brandType || '').toUpperCase();
            const brandTypeName = String(this.brandDetail.brandTypeName || '').toUpperCase();
            return brandType === 'BRAND_ONLINE'
                || brandType === 'ONLINE'
                || brandTypeName.includes('온라인')
                || brandTypeName.includes('ONLINE');
        },
        displayStoreList() {
            return this.isOnlineBrand ? [{ ...this.brandDetail, storeUuid: 'online-brand' }] : this.storeList;
        },
        resolvedBrandUuid() {
            return this.$route?.params?.brandUuid || sessionStorage.getItem("brandUuid") || this.brandUuid || '';
        },
        displayBrandName() {
            return this.brandDetail.brandName || '제휴사';
        },
        discountTitle() {
            const benefit = this.brandDetail.benefit;
            return benefit ? `${benefit}% 청구할인` : '청구할인';
        },
        benefitDescription() {
            const remarks = this.stripHtml(this.brandDetail.brandRmks);
            if (remarks) return remarks;
            return this.brandDetail.benefit ? `결제 시 ${this.brandDetail.benefit}% 청구할인` : '청구할인 혜택';
        },
        hasMoreStores() {
            return this.storeList.length > 0 && this.storeList.length < this.total;
        },
        safeBrandDetailView() {
            let html = this.brandDetail.brandDetailView || '';
            html = html.replace(/<a\s+[^>]*href=(["'])\s*\1[^>]*>(.*?)<\/a>/gi, '$2');
            html = html.replace(/<a\s+href=(["'])\s*\1[^>]*>(.*?)<\/a>/gi, '$2');
            return html;
        },
    },
    watch: {
        '$route.params.brandUuid'(newVal) {
            if (newVal) {
                sessionStorage.setItem("brandUuid", newVal);
                this.fetchBrandDetail();
                this.fetchAvailableRegions();
                this.resetStoreSearch();
                this.fetchStores();
            }
        },
    },
    mounted() {
        this.updateFooterHeight();
        if (typeof ResizeObserver !== 'undefined') {
            this.footerResizeObserver = new ResizeObserver(this.updateFooterHeight);
            this.footerResizeObserver.observe(this.$refs.nhFooter);
        }
        if (this.resolvedBrandUuid) {
            sessionStorage.setItem("brandUuid", this.resolvedBrandUuid);
        }
        this.fetchBrandDetail();
        this.fetchAvailableRegions();
        this.fetchStores();
    },
    beforeUnmount() {
        if (this.footerResizeObserver) {
            this.footerResizeObserver.disconnect();
        }
    },
    methods: {
        updateFooterHeight() {
            this.footerHeight = this.$refs.nhFooter?.offsetHeight || 0;
        },
        async fetchBrandDetail() {
            try {
                this.isLoading.brand = true;
                const response = await getAxios("/hvm/v1/branch/brand", {
                    page: 1,
                    size: 10,
                    brandUuid: this.resolvedBrandUuid,
                });

                const detail = response?.page?.list?.[0];
                if (!detail) return;

                this.brandDetail = {
                    brandName: detail.brandName,
                    brandRmks: detail.brandRmks,
                    brandType: detail.brandType,
                    brandTypeName: detail.brandTypeName,
                    homepageUrl: detail.homepageUrl,
                    benefit: detail.benefit,
                    benefitCard: detail.benefitCard,
                    phone: detail.phone,
                    brandPath: detail.logoPath,
                    brandDetailView: detail.brandDetail,
                    storeCnt: detail.storeCnt,
                    nhYn: detail.nhYn,
                    hanaYn: detail.hanaYn,
                    bcYn: detail.bcYn,
                    shYn: detail.shYn,
                    lotteYn: detail.lotteYn,
                    smYn: detail.smYn,
                    kbYn: detail.kbYn,
                    hdYn: detail.hdYn,
                    weYn: detail.weYn,
                };
            } catch (error) {
                console.error("브랜드 상세 데이터 로드 실패:", error);
            } finally {
                this.isLoading.brand = false;
            }
        },
        async fetchStores({ append = false } = {}) {
            try {
                this.isLoading.search = true;
                this.selectedStoreIndex = -1;
                this.mapLoadedMap = {};
                this.mapReadyMap = {};

                const params = {
                    page: this.currentPage,
                    size: this.pageSize,
                    branchUuid: this.branchUuid,
                    brandUuid: this.resolvedBrandUuid,
                };

                if (this.selectedRegion) {
                    params.sido = this.selectedRegion;
                }

                const response = await getAxios("/hvm/v1/branch/store", params);
                const nextList = response?.page?.list || [];
                this.total = response?.page?.total || nextList.length;
                this.storeList = append ? [...this.storeList, ...nextList] : nextList;
            } catch (error) {
                console.error("매장 데이터 로드 실패:", error);
                if (!append) {
                    this.storeList = [];
                    this.total = 0;
                }
            } finally {
                this.isLoading.search = false;
            }
        },
        async fetchAvailableRegions() {
            try {
                const response = await getAxios('/hvm/v1/branch/store/region/list', {
                    branchUuid: this.branchUuid,
                    brandUuid: this.resolvedBrandUuid,
                });
                const regions = response?.page?.list || [];
                this.availableRegions = [...new Set(regions
                    .map(region => region.regionName)
                    .filter(Boolean))];

                if (this.selectedRegion && !this.availableRegions.includes(this.selectedRegion)) {
                    this.selectedRegion = '';
                }
            } catch (error) {
                console.error('매장 지역 데이터 로드 실패:', error);
                this.availableRegions = [];
            }
        },
        selectRegion(region) {
            if (this.selectedRegion === region) return;
            this.selectedRegion = region;
            this.resetStoreSearch();
            this.fetchStores();
        },
        loadMoreStores() {
            if (!this.hasMoreStores || this.isLoading.search) return;
            this.currentPage += 1;
            this.fetchStores({ append: true });
        },
        resetStoreSearch() {
            this.currentPage = 1;
            this.total = 0;
            this.storeList = [];
            this.selectedStoreIndex = -1;
        },
        toggleStoreMap(index) {
            if (this.isOnlineBrand) return;

            if (this.selectedStoreIndex === index) {
                this.selectedStoreIndex = -1;
                return;
            }

            const store = this.storeList[index];
            if (!store?.latitude || !store?.longitude || store.latitude === '0' || store.longitude === '0') {
                alert('해당 매장의 위치 정보가 없습니다.');
                return;
            }

            this.mapLoadedMap = {};
            this.mapReadyMap = {};
            this.selectedStoreIndex = index;
            setTimeout(() => {
                this.mapReadyMap = { ...this.mapReadyMap, [index]: true };
                window.dispatchEvent(new Event('resize'));
            }, 120);
        },
        onMapLoaded(index) {
            this.mapLoadedMap = { ...this.mapLoadedMap, [index]: true };
        },
        handleMapError(index) {
            this.mapReadyMap = { ...this.mapReadyMap, [index]: false };
        },
        makeBrandImageUrl(imagePath) {
            return imagePath ? `${process.env.VUE_APP_IMAGE_BASE_URL}images/${imagePath}` : DEFAULT_NO_IMAGE;
        },
        formatHomepageUrl(url) {
            const value = String(url || '').trim();
            if (!value) return value;

            const isHttpOnlyBrand = this.resolvedBrandUuid === 'c23873a0-467a-48a8-91cf-f9a7b181338b';
            if (isHttpOnlyBrand) {
                return value.replace(/^https?:\/\//i, 'http://').replace(/^(?!http:\/\/)/i, 'http://');
            }

            return !/^https?:\/\//i.test(value) ? `https://${value}` : value;
        },
        openHomepage(url) {
            if (!url || url.trim() === '') {
                alert('홈페이지 정보가 없습니다.');
                return;
            }

            const validUrl = this.formatHomepageUrl(url);
            window.open(validUrl, '_blank', 'noopener,noreferrer');
        },
        stripHtml(value) {
            if (!value) return '';
            return String(value).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        },
        goBackToNhMain() {
            window.history.back();
        },
        goBackToList() {
            window.history.back();
        },
    },
};
</script>

<style lang="scss" scoped>
.nh-billing-page {
    min-height: 100vh;
    padding-bottom: calc(var(--nh-footer-height, 120px) + 90px);
    background: #fff;
    color: #222;
    font-family: "Pretendard", "Noto Sans KR", sans-serif;
    -webkit-text-size-adjust: none;
    text-size-adjust: none;
}

.nh-page-header {
    display: flex;
    align-items: center;
    gap: 14px;
    max-width: 720px;
    margin: 0 auto;
    padding: 40px 40px 34px;
}

.nh-page-header h1 {
    margin: 0;
    font-size: 30px;
    font-weight: 800;
    line-height: 1.2;
}

.nh-back-button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border: 0;
    background: none;
    color: #111;
    cursor: pointer;
}

.nh-back-button svg {
    display: block;
    width: 28px;
    height: 28px;
}

.nh-page-body {
    max-width: 720px;
    margin: 0 auto;
    padding: 0 40px 44px;
}

.nh-hero-section {
    padding: 58px 0 0;
}

.nh-brand-name {
    margin: 0 0 14px;
    color: #777;
    font-size: 30px;
    font-weight: 700;
    line-height: 1.25;
}

.nh-benefit-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 28px;
}

.nh-title-box {
    min-width: 0;
}

.nh-benefit-row h2 {
    margin: 0;
    font-size: 32px;
    font-weight: 800;
    line-height: 1.2;
    letter-spacing: 0;
}

.nh-logo-box {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 80px;
    width: 80px;
    height: 80px;
}

.nh-logo-box img {
    display: block;
    width: 80px;
    height: 80px;
    object-fit: contain;
}

.nh-summary-card {
    display: grid;
    gap: 34px;
    margin-bottom: 72px;
    padding: 52px 50px;
    border-radius: 24px;
    background: #f5f7fb;
}

.nh-summary-item strong {
    display: block;
    margin-bottom: 16px;
    font-size: 28px;
    font-weight: 800;
    line-height: 1.2;
}

.nh-summary-item p,
.nh-muted-text {
    margin: 0;
    color: #666;
    font-size: 25px;
    font-weight: 500;
    line-height: 1.45;
}

.nh-homepage-link {
    display: inline-block;
    max-width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    color: #06f;
    font-size: 25px;
    font-weight: 500;
    line-height: 1.35;
    text-align: left;
    overflow-wrap: anywhere;
    word-break: break-word;
    white-space: normal;
    cursor: pointer;
}

.nh-tab-list {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin: 0 -40px;
    border-bottom: 1px solid #d9d9d9;
}

.nh-tab-list button {
    position: relative;
    padding: 22px 8px 18px;
    border: 0;
    background: #fff;
    color: #999;
    font-size: 27px;
    font-weight: 500;
    line-height: 1.25;
    letter-spacing: 0;
    cursor: pointer;
}

.nh-tab-list button.active {
    color: #333;
    font-weight: 800;
}

.nh-tab-list button.active::after {
    content: "";
    position: absolute;
    right: 40px;
    bottom: -1px;
    left: 40px;
    height: 4px;
    background: #111;
}

.nh-detail-panel {
    padding: 102px 0 0;
}

.nh-brand-detail {
    overflow: hidden;
    text-align: center;
}

.nh-brand-detail :deep(img) {
    max-width: 100%;
    height: auto !important;
}

.nh-empty-detail {
    padding: 8px 0 40px;
    text-align: center;
}

.nh-empty-detail p {
    margin: 0 0 10px;
    color: #333;
    font-size: 20px;
    font-weight: 500;
    line-height: 1.4;
}

.nh-empty-detail strong {
    display: block;
    font-size: 32px;
    font-weight: 800;
    line-height: 1.38;
}

.nh-store-panel {
    padding-top: 40px;
}

.nh-region-list {
    position: relative;
    display: grid;
    gap: 12px;
    padding: 0 58px 0 3px;
    margin-bottom: 44px;
}

.nh-region-row {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    align-items: center;
    gap: 12px 14px;
}

.nh-region-row:first-child {
    grid-template-columns: repeat(5, minmax(0, 1fr));
}

.nh-region-row+.nh-region-row {
    grid-template-columns: repeat(5, minmax(0, 1fr));
}

.nh-region-list button {
    width: 100%;
    min-width: 0;
    height: 44px;
    padding: 0 22px;
    border: 1px solid #e0e0e0;
    border-radius: 24px;
    background: #fff;
    color: #878787;
    font-family: "Pretendard", sans-serif;
    font-size: 15px;
    font-weight: 600;
    line-height: 1;
    cursor: pointer;
}

.nh-region-row .nh-region-more-button {
    position: absolute;
    top: 0;
    right: 0;
    width: 44px;
    min-width: 44px;
    margin-left: 0;
}

.nh-region-list button.active {
    border-color: #222;
    background: #222;
    color: #fff;
}

.nh-region-list .nh-region-more-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    width: 44px;
    padding: 0;
    border-color: #dfe3e9;
    border-radius: 50%;
    background: #fff;
    margin-left: -11px;
}

.nh-region-more-button span {
    width: 10px;
    height: 10px;
    border-right: 3px solid #888;
    border-bottom: 3px solid #888;
    transform: translateY(-3px) rotate(45deg);
    transition: transform 0.2s ease;
}

.nh-region-more-button.expanded span {
    transform: translateY(3px) rotate(225deg);
}

.nh-region-list.expanded {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px 14px;
    padding-right: 58px;
}

.nh-region-list.expanded .nh-region-row {
    display: contents;
}

.nh-region-list.expanded .nh-region-more-button {
    margin-left: 0;
}

.nh-store-list {
    margin: 0;
    padding: 0;
    list-style: none;
}

.nh-store-item {
    padding: 26px 0 24px;
    border-bottom: 1px solid #eee;
}

.nh-store-main {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
}

.nh-store-copy {
    min-width: 0;
}

.nh-store-copy strong {
    display: block;
    margin-bottom: 12px;
    color: #4d4d4d;
    font-family: "Pretendard", sans-serif;
    font-size: 17px;
    font-weight: 700;
    line-height: 1.25;
}

.nh-store-copy p {
    margin: 0 0 14px;
    color: #878787;
    font-family: "Pretendard", sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.4;
}

.nh-store-copy span {
    display: block;
    color: #4d4d4d;
    font-family: "Pretendard", sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.2;
}

.nh-online-store-item .nh-store-copy strong {
    color: #111;
}

.nh-online-store-main {
    display: block;
}

.nh-online-store-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    margin-bottom: 12px;
}

.nh-online-store-title-row strong {
    color: #111;
    font-family: "Pretendard", sans-serif;
    font-size: 22px;
    font-weight: 800;
    line-height: 1.25;
}

.nh-online-homepage {
    display: block;
    max-width: 100%;
    margin: 0 0 14px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #4776c8;
    font-family: "Pretendard", sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.4;
    text-align: left;
    overflow-wrap: anywhere;
    word-break: break-word;
    white-space: normal;
    cursor: pointer;
}

.nh-online-store-main>span {
    display: block;
    color: #111;
    font-family: "Pretendard", sans-serif;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.2;
}

.nh-map-button {
    flex: 0 0 auto;
    min-width: 96px;
    height: 38px;
    padding: 0 17px;
    border: 1px solid #1f73ff;
    border-radius: 20px;
    background: #fff;
    color: #2970e6;
    font-family: "Pretendard", sans-serif;
    font-size: 15px;
    font-weight: 600;
    line-height: 1;
    cursor: pointer;
}

.nh-map-button.disabled,
.nh-map-button:disabled {
    border-color: #c9d8f2;
    background: #fff;
    color: #4776c8;
    cursor: default;
    pointer-events: none;
}

.nh-map-area {
    position: relative;
    margin-top: 22px;
    overflow: hidden;
    border-radius: 18px;
}

.nh-map-loading {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.78);
    color: #555;
    font-size: 15px;
}

.nh-store-loading,
.nh-empty-store {
    padding: 86px 0 120px;
    color: #555;
    font-size: 23px;
    font-weight: 600;
    line-height: 1.45;
    text-align: center;
}

.nh-more-button {
    display: block;
    width: calc(100% + 80px);
    height: 58px;
    margin: 28px -40px 54px;
    border: 0;
    background: #f7f7f7;
    color: #333;
    font-size: 19px;
    font-weight: 800;
    cursor: pointer;
}

.nh-more-button::after {
    content: "";
    display: inline-block;
    width: 9px;
    height: 9px;
    margin: 0 0 4px 9px;
    border-right: 2px solid currentColor;
    border-bottom: 2px solid currentColor;
    transform: rotate(45deg);
}

.nh-bottom-action {
    position: fixed;
    /* 상단 요약 카드 우측 라인(본문 max-width 720px, 좌우 padding 40px)과 정렬 */
    right: max(40px, calc((100vw - 720px) / 2 + 40px));
    bottom: calc(var(--nh-footer-height, 142px) + 12px);
    z-index: 210;
    width: 58px;
    height: 58px;
    margin: 0;
    padding: 0;
}

.nh-bottom-action button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 58px;
    height: 58px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.15s ease;
}

.nh-bottom-action button:active {
    transform: scale(0.94);
}

.nh-bottom-action button img {
    display: block;
    width: 100%;
    height: 100%;
}

.nh-floating-footer {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 100;
    border-top: 3px solid #1d2d52;
    background: #fff;
    box-shadow: 0 -8px 24px rgba(13, 31, 66, 0.08);
}

.nh-footer-inner {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 240px;
    gap: 28px;
    max-width: 720px;
    margin: 0 auto;
    padding: 14px 40px 13px;
}

.nh-footer-company strong,
.nh-footer-cs strong {
    display: block;
    margin-bottom: 8px;
    color: #111;
    font-size: 17px;
    font-weight: 800;
    line-height: 1.25;
}

.nh-footer-company dl {
    display: flex;
    flex-wrap: wrap;
    gap: 3px 14px;
    margin: 0;
    color: #333;
    font-size: 11px;
    line-height: 1.35;
}

.nh-footer-company dl div {
    display: flex;
    align-items: center;
    gap: 7px;
}

.nh-footer-company dl .nh-footer-meta {
    flex-basis: 100%;
    flex-wrap: nowrap;
    gap: 8px;
    white-space: nowrap;
}

.nh-footer-company dl .nh-footer-meta>div {
    gap: 5px;
}

.nh-footer-divider {
    color: #999;
}

.nh-footer-company dl div.address {
    flex-basis: 100%;
    display: block;
}

.nh-address-mobile {
    display: none;
}

.nh-footer-company dt,
.nh-footer-company dd {
    margin: 0;
    font-weight: 500;
}

.nh-footer-company p {
    margin: 7px 0 0;
    color: #333;
    font-size: 10px;
    font-weight: 500;
    line-height: 1.3;
}

.nh-footer-cs {
    min-width: 0;
}

.nh-cs-phone {
    display: flex;
    align-items: center;
    gap: 12px;
    color: #111;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.15;
    text-decoration: none;
}

.nh-phone-icon {
    position: relative;
    flex: 0 0 auto;
    width: 24px;
    height: 38px;
    border: 4px solid #111;
    border-radius: 4px;
}

.nh-phone-icon::before {
    content: "";
    position: absolute;
    top: 5px;
    left: 50%;
    width: 9px;
    height: 3px;
    border-radius: 4px;
    background: #111;
    transform: translateX(-50%);
}

@media (max-width: 767px) {
    .nh-billing-page {
        padding-bottom: calc(var(--nh-footer-height, 126px) + 80px);
    }

    .nh-page-header {
        box-sizing: border-box;
        height: 57px;
        padding: 0 16px;
        gap: 0;
        border-bottom: 1px solid #f0f0f0;
    }

    .nh-page-header h1 {
        margin-left: 8px;
        color: #111;
        font-size: 18px;
        font-weight: 700;
        line-height: normal;
    }

    .nh-back-button svg {
        width: 20px;
        height: 20px;
    }

    .nh-page-body {
        padding: 0 20px 34px;
    }

    .nh-hero-section {
        margin-bottom: 24px;
        padding: 24px 0 0;
    }

    .nh-brand-name {
        margin: 0;
        color: #111;
        font-size: 16px;
        font-weight: 400;
        line-height: 23px;
    }

    .nh-benefit-row {
        flex-direction: column;
        align-items: flex-end;
        gap: 0;
    }

    .nh-title-box {
        width: 100%;
    }

    .nh-benefit-row h2 {
        margin: 20px 0;
        color: #111;
        font-size: 24px;
        font-weight: 700;
        line-height: 35px;
    }

    .nh-logo-box {
        flex: 0 0 auto;
        width: 80px;
        height: 80px;
        margin-top: 16px;
        align-self: flex-end;
    }

    .nh-logo-box img {
        width: 80px;
        height: 80px;
    }

    .nh-summary-card {
        display: block;
        margin-bottom: 28px;
        padding: 24px 20px;
        border-radius: 16px;
        background: #f7f9fc;
    }

    .nh-summary-item + .nh-summary-item {
        margin-top: 20px;
    }

    .nh-summary-item strong {
        margin-bottom: 8px;
        color: #111;
        font-size: 15px;
        font-weight: 700;
        line-height: 22px;
    }

    .nh-summary-item p {
        margin: 14px 0;
        color: #555;
        font-size: 14px;
        line-height: 1.4;
    }

    .nh-muted-text,
    .nh-homepage-link {
        display: block;
        width: 100%;
        font-size: 14px;
        line-height: 23px;
        word-break: break-all;
        overflow-wrap: normal;
    }

    .nh-muted-text {
        color: #555;
    }

    .nh-homepage-link {
        color: #1a73e8;
    }

    .nh-tab-list {
        margin: 0 -20px;
        padding: 0 20px;
        border-bottom-color: #e0e0e0;
    }

    .nh-tab-list button {
        padding: 14px 0;
        color: #888;
        font-size: 15px;
        font-weight: 600;
        line-height: 22px;
    }

    .nh-tab-list button.active {
        color: #111;
        font-weight: 700;
    }

    .nh-tab-list button.active::after {
        right: 0;
        left: 0;
        height: 2px;
    }

    .nh-detail-panel {
        padding-top: 40px;
    }

    .nh-brand-detail :deep(img) {
        display: block;
        width: 100%;
        margin: 0 auto;
    }

    .nh-empty-detail strong {
        font-size: 24px;
    }

    .nh-store-panel {
        padding-top: 32px;
    }

    .nh-region-list {
        gap: 10px 11px;
        padding-right: 52px;
        margin-bottom: 34px;
    }

    .nh-region-row {
        gap: 10px 3px;
    }

    .nh-region-list button {
        min-width: 0;
        height: 40px;
        padding: 0 4px;
        font-size: 14px;
    }

    .nh-region-list .nh-region-more-button {
        min-width: 48px;
        width: 48px;
        margin-left: 0;
    }

    .nh-region-row:first-child {
        grid-template-columns: repeat(5, minmax(0, 1fr));
    }

    .nh-region-row+.nh-region-row {
        grid-template-columns: repeat(5, minmax(0, 1fr));
    }

    .nh-region-list.expanded {
        gap: 10px 8px;
        padding-right: 52px;
    }

    .nh-region-list.expanded .nh-region-more-button {
        width: 48px;
        min-width: 48px;
    }

    .nh-store-item {
        padding: 23px 0 22px;
    }

    .nh-store-copy strong {
        font-size: 17px;
    }

    .nh-store-copy p {
        font-size: 14px;
    }

    .nh-store-copy span {
        font-size: 16px;
    }

    .nh-map-button {
        min-width: 82px;
        height: 36px;
        padding: 0 13px;
        font-size: 15px;
    }

    .nh-store-loading,
    .nh-empty-store {
        padding: 72px 0 110px;
        font-size: 18px;
    }

    .nh-more-button {
        width: calc(100% + 40px);
        margin: 24px -20px 42px;
    }

    .nh-bottom-action {
        right: 20px;
        bottom: calc(var(--nh-footer-height, 126px) + 12px);
    }

    .nh-floating-footer {
        border-top-width: 2px;
    }

    .nh-footer-inner {
        grid-template-columns: minmax(0, 1fr) auto;
        gap: 12px;
        padding: 12px 20px 11px;
    }

    .nh-footer-company strong,
    .nh-footer-cs strong {
        margin-bottom: 6px;
        font-size: 13px;
    }

    .nh-footer-company dl {
        gap: 2px 8px;
        font-size: 9px;
        line-height: 1.25;
    }

    .nh-footer-company dl .nh-footer-meta {
        gap: 6px;
        flex-wrap: wrap;
        white-space: normal;
    }

    .nh-footer-company dl .nh-footer-meta>div {
        gap: 4px;
    }

    .nh-footer-company dl div.address {
        display: block;
    }

    .nh-address-desktop {
        display: none;
    }

    .nh-address-mobile {
        display: inline;
    }

    .nh-footer-company p {
        margin-top: 5px;
        font-size: 8px;
    }

    .nh-cs-phone {
        justify-content: flex-end;
        gap: 4px;
        font-size: 11px;
        white-space: nowrap;
    }

    .nh-footer-cs {
        margin-right: 3px;
        text-align: right;
    }

    .nh-phone-icon {
        width: 18px;
        height: 29px;
        border-width: 3px;
    }

    .nh-phone-icon::before {
        top: 3px;
        width: 7px;
        height: 2px;
    }
}
</style>
